import {
  IonButton,
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  apiUrpassGetAgencies,
  apiUrpassGetPasses,
  apiUrpassPurchasePass,
} from '../services/urpass';
import UrpassAdd from '../usecases/urpass/UrpassAdd';
import UrpassAgencies from '../usecases/urpass/UrpassAgencies';
import UrpassPasses from '../usecases/urpass/UrpassPasses';
import UrpassBuy from '../usecases/urpass/UrpassBuy';
import PaymentsList, {AdditionalPaymentMethod} from '../usecases/payments/PaymentsList';
import AppSeparator from '../components/AppSeparator';
import { PaymentMethod } from '../services/types/apitypes';
import { apiGetPaymentMethods } from '../services/api';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import PaymentItem from '../usecases/payments/PaymentItem';
import PaymentAddProcess from '../usecases/shared/PaymentAddProcess';
import PaymentAddButton from '../usecases/payments/PaymentAddButton';
import { currencyFormat } from '../utils/currency';
import { UrPassAgency, UrPassProduct } from '../services/types/urpassapitypes';
import { LoadingController } from '../utils/LoadingController';
import {
  storageGetUrpassTenants,
  storageSetUrpassTenants,
} from '../utils/storage';
import { useToaster } from '../hooks/useToaster';
import {getAdditionalPaymentMethods} from "../hooks/usecollectJS";

const UrpassaddPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const router = useHistory();
  const [present] = useToaster();
  const [t] = useTranslation('global');

  const [block, setBlock] = useState<string>('start');

  const [agencies, setAgencies] = useState<UrPassAgency[] | undefined>(
    storageGetUrpassTenants(),
  );
  const [agency, setAgency] = useState<UrPassAgency | undefined>(undefined);
  const [agenciesError, setAgenciesError] = useState<boolean>(false);

  const [passes, setPasses] = useState<UrPassProduct[] | undefined>(undefined);
  const [pass, setPass] = useState<UrPassProduct | undefined>(undefined);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | AdditionalPaymentMethod | undefined
  >(undefined);

  const loadDataFromApi = async () => {
    LoadingController.show();
    try {
      const response = await apiUrpassGetAgencies(auth);
      setAgencies(response.data);
      storageSetUrpassTenants(response.data);
      setAgenciesError(false);
    } catch (e) {
      console.log('Error occurred while retrieving data', e);
      setAgenciesError(true);
      // we still have cached agencies to work with, so only notify
      if (agencies && agencies.length > 0) {
        present(getAppError(t('urpassadd.agenciesnosuccess')));
      }
    } finally {
      LoadingController.hide();
    }
  };

  const loadPayments = async () => {
    try {
      LoadingController.show();
      const response = await apiGetPaymentMethods(auth);
      setPaymentMethods(response.data);
      const defaultMethod = response.data.find((e) => e.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod);
      } else {
        if (response.data.length > 0) {
          setSelectedPaymentMethod(response.data[0]);
        }
      }
    } catch (error) {
      console.log('Error loading payments', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardpasses.paymentsnosuccess')));
      }
    } finally {
      LoadingController.hide();
    }
  };

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    loadDataFromApi();
    setBlock('start');
    setPass(undefined);
    setAgency(undefined);
    setPasses(undefined);
    setSelectedPaymentMethod(undefined);
    setPaymentMethods([]);
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadDataFromApi().then(() => e.detail.complete());
  };

  const selectAgency = async (agency: UrPassAgency) => {
    LoadingController.show();
    setAgency(agency);
    setBlock('start');
    try {
      const response = await apiUrpassGetPasses(auth, agency);
      setPasses(response.data.map((p) => ({ ...p, price: p.price / 100 })));
    } catch (e) {
      console.log('Error occurred while retrieving data', e);
    } finally {
      LoadingController.hide();
    }
  };

  const selectPass = (pass: any) => {
    setPass(pass);
    setBlock('buy');
    if (!selectedPaymentMethod) {
      loadPayments();
    }
  };

  const returnFromAddPayment = async () => {
    await loadPayments().then(() => console.log('Loaded payments'));
    setBlock('selectpayment');
  };

  const selectPaymentMethod = (p: PaymentMethod | AdditionalPaymentMethod) => {
    setSelectedPaymentMethod(p);
    setBlock('buy');
  };

  const payNow = async (
    pass: UrPassProduct,
    selectedPaymentMethod: PaymentMethod,
    agency: UrPassAgency,
  ) => {
    LoadingController.show();

    try {
      await apiUrpassPurchasePass(
        auth,
        agency?.id,
        selectedPaymentMethod?.id,
        pass?.offerToken,
      );

      router.push('urpass');
      present(getAppSuccess(t('urpassadd.paymentssuccess')));
    } catch (error) {
      console.log('Error purchasing pass', error);
      present(getAppError(t('urpassadd.paymentsnosuccess')));
    } finally {
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="urpass-page">
      {block === 'start' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => router.push('/urpass')}
            title={t('urpass.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <IonRefresher slot="fixed" onIonRefresh={refresh}>
              <IonRefresherContent></IonRefresherContent>
            </IonRefresher>
            {agenciesError && !agencies?.length ? (
              <>
                <div className="pt-5 ion-text-center">
                  <p>{t('urpassadd.agenciesnosuccess')}</p>
                </div>
                <div className="pt-3 ion-text-center">
                  <IonButton
                    className="btn-default btn-wide-80"
                    onClick={() => loadDataFromApi()}
                  >
                    {t('home.retry')}
                  </IonButton>
                </div>
              </>
            ) : (
              <>
                <UrpassAdd
                  agency={agency}
                  changeAgency={() => setBlock('agency')}
                  agencies={agencies}
                />
                <UrpassPasses
                  passes={passes}
                  selectPass={selectPass}
                  icon={agency?.id || ''}
                />
              </>
            )}
          </IonContent>
        </>
      )}
      {block === 'agency' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('start')}
            title={t('urpass.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <IonRefresher slot="fixed" onIonRefresh={refresh}>
              <IonRefresherContent></IonRefresherContent>
            </IonRefresher>
            <UrpassAgencies agencies={agencies} setAgency={selectAgency} />
          </IonContent>
        </>
      )}
      {block === 'buy' && pass && agency && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('start')}
            title={t('urpass.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <UrpassBuy pass={pass} agency={agency} />
            <AppSeparator />
            {selectedPaymentMethod ? (
              <>
                {' '}
                <h1 className="h1 pb-1">{t('urpassadd.paymentmethod')}</h1>
                {typeof selectedPaymentMethod === 'string' ? (
                    <PaymentItem click={() => setBlock('selectpayment')} otherPaymentType={selectedPaymentMethod} />
                ) :
                <PaymentItem
                  payment={selectedPaymentMethod}
                  click={() => setBlock('selectpayment')}
                />}
                <h1 className="h1 pb-3">
                  {t('urpassadd.total')}: {currencyFormat(pass?.price || 0)}
                </h1>
                <div className="pt-3 ion-text-center">
                  {typeof selectedPaymentMethod !== "string" ?
                  <IonButton
                    className="btn-default btn-wide-80"
                    onClick={() => payNow(pass, selectedPaymentMethod, agency)}
                  >
                    {t('urpassadd.paynow')}
                  </IonButton> : <><div className={"platform-button"} /> </>}
                </div>
              </>
            ) : (
              <PaymentAddButton click={() => setBlock('addpayment')} />
            )}
          </IonContent>
        </>
      )}
      {block === 'addpayment' && (
        <PaymentAddProcess
          isPageActive={isPageActive}
          callback={returnFromAddPayment}
        />
      )}
      {block === 'selectpayment' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('buy')}
            title={t('urpass.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <PaymentsList
              payments={paymentMethods}
              selectPayment={selectPaymentMethod}
              additionalPayments={[/* disabled until we get the callback */]}
            />
            <AppSeparator />
            <PaymentAddButton click={() => setBlock('addpayment')} />
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default UrpassaddPage;
